/* eslint-disable */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Landmark, RefreshCw, Search } from 'lucide-react'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../Components/ui/tabs'
import { Card, CardContent } from '../../Components/ui/card'
import { Button } from '../../Components/ui/button'
import { Input } from '../../Components/ui/input'
import { Skeleton } from '../../Components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../Components/ui/table'
import CreateMandate from './CreateMandate'
import env from '../../env/env'
import { get } from '../../http/restservice'
import { toCurrency } from '../../utils/CurrencyUtil'
import { capitalizeWords } from '../../utils/Util'
import { cn } from '../../lib/utils'

const MANDATES_ENDPOINT = `${env.API_URL}mutualfundservice/api/mutualFund/mandates`

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'approved', label: 'Approved' },
  { id: 'pending', label: 'Pending' },
  { id: 'rejected', label: 'Rejected' },
]

const getStatusGroup = (status) => {
  const normalized = String(status || '').trim().toUpperCase()
  if (['APPROVED', 'ACTIVE'].includes(normalized)) return 'approved'
  if (['REJECTED', 'FAILED', 'CANCELLED', 'CANCELLED BY MEMBER'].includes(normalized)) {
    return 'rejected'
  }
  return 'pending'
}

const getStatusStyles = (status) => {
  const group = getStatusGroup(status)
  if (group === 'approved') {
    return 'border-[var(--fb-positive)]/20 bg-[var(--fb-positive-bg)] text-[var(--fb-positive)]'
  }
  if (group === 'rejected') {
    return 'border-[var(--fb-danger-border)] bg-[var(--fb-danger-bg)] text-[var(--fb-danger)]'
  }
  return 'border-[var(--fb-warning-border)] bg-[var(--fb-warning-bg)] text-[var(--fb-warning)]'
}

const formatStatusLabel = (status) => {
  const normalized = String(status || '').trim().toUpperCase()
  if (
    normalized === 'WAITING FOR CLIENT AUTHENTICATION' ||
    normalized === 'REQUESTED' ||
    normalized === 'NEW'
  ) {
    return 'Awaiting approval'
  }
  if (normalized === 'RECEIVED BY EXCHANGE') return 'In progress'
  if (normalized === 'CANCELLED BY MEMBER') return 'Cancelled'
  return capitalizeWords(String(status || '—').toLowerCase())
}

const maskAccount = (accountNumber) => {
  const value = String(accountNumber || '')
  if (value.length < 4) return value || '—'
  return `•••• ${value.slice(-4)}`
}

const formatCreatedAt = (value) => {
  if (!value) return '—'
  const parsed = Date.parse(String(value).replace(' ', 'T'))
  if (Number.isNaN(parsed)) return value
  return new Date(parsed).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const getBankLogo = (ifscCode) => {
  const branch = ifscCode?.match(/^[A-Za-z]{4}/)?.[0]?.toLowerCase()
  if (!branch) return null
  return `${env.BANK_LOGOS}/${branch}/symbol.svg`
}

const UserMandatesList = ({ clientCode, refreshKey }) => {
  const [mandates, setMandates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const fetchMandates = useCallback(async () => {
    if (!clientCode) {
      setMandates([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')
    try {
      const response = await get(MANDATES_ENDPOINT, {}, { userId: clientCode })
      if (response?.isError) {
        throw new Error(response?.response?.message || 'Failed to fetch mandates')
      }
      const list = Array.isArray(response?.data) ? response.data : []
      const sorted = [...list].sort((a, b) => {
        const left = Date.parse(String(b?.createdTimeStamp || '').replace(' ', 'T')) || 0
        const right = Date.parse(String(a?.createdTimeStamp || '').replace(' ', 'T')) || 0
        return left - right
      })
      setMandates(sorted)
    } catch (e) {
      setError(e?.message || 'Unable to load mandates')
      setMandates([])
    } finally {
      setLoading(false)
    }
  }, [clientCode])

  useEffect(() => {
    fetchMandates()
  }, [fetchMandates, refreshKey])

  const counts = useMemo(
    () =>
      mandates.reduce(
        (acc, mandate) => {
          acc.total += 1
          acc[getStatusGroup(mandate.mandateStatus)] += 1
          return acc
        },
        { total: 0, approved: 0, pending: 0, rejected: 0 },
      ),
    [mandates],
  )

  const filteredMandates = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    return mandates.filter((mandate) => {
      const matchesStatus =
        statusFilter === 'all' || getStatusGroup(mandate.mandateStatus) === statusFilter
      if (!matchesStatus) return false
      if (!query) return true
      return [
        mandate.mandateId,
        mandate.accountNumber,
        mandate.ifscCode,
        mandate.mandateStatus,
        mandate.mandateType,
        mandate.amount,
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    })
  }, [mandates, searchQuery, statusFilter])

  const summaryCards = [
    { label: 'Total', value: counts.total },
    { label: 'Approved', value: counts.approved },
    { label: 'Pending', value: counts.pending },
    { label: 'Rejected', value: counts.rejected },
  ]

  if (loading) {
    return (
      <div className='flex flex-col gap-4'>
        <div className='grid grid-cols-2 gap-3 lg:grid-cols-4'>
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className='h-24 rounded-2xl bg-[var(--fb-surface-muted)]' />
          ))}
        </div>
        <Skeleton className='h-80 rounded-2xl bg-[var(--fb-surface-muted)]' />
      </div>
    )
  }

  if (error) {
    return (
      <div className='rounded-2xl border border-dashed border-[var(--fb-border)] bg-[var(--fb-surface-muted)] px-5 py-10 text-center'>
        <p className='font-DMSans text-base font-semibold text-[var(--fb-text-strong)]'>
          Unable to load mandates
        </p>
        <p className='mt-1 text-sm text-[var(--fb-text-muted)]'>{error}</p>
        <Button
          type='button'
          variant='outline'
          onClick={fetchMandates}
          className='mt-4 h-10 rounded-xl border-[var(--fb-border)]'
        >
          Try again
        </Button>
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='grid grid-cols-2 gap-3 lg:grid-cols-4'>
        {summaryCards.map((card) => (
          <Card
            key={card.label}
            className='border border-[var(--fb-border)] bg-[var(--fb-surface)] shadow-[var(--fb-shadow-sm)]'
          >
            <CardContent className='p-4 md:p-5'>
              <p className='text-xs font-medium uppercase tracking-[0.08em] text-[var(--fb-text-muted)]'>
                {card.label}
              </p>
              <p className='mt-2 font-DMSans text-2xl font-bold tracking-tight text-[var(--fb-text-strong)]'>
                {card.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className='flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
        <div className='relative w-full md:max-w-sm'>
          <Search className='pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[var(--fb-text-muted)]' />
          <Input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder='Search mandate ID, account, or IFSC'
            className='h-10 rounded-xl border-[var(--fb-border)] bg-[var(--fb-surface)] pl-9'
          />
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          {STATUS_FILTERS.map((filter) => (
            <button
              key={filter.id}
              type='button'
              onClick={() => setStatusFilter(filter.id)}
              className={cn(
                'rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                statusFilter === filter.id
                  ? 'border-[var(--fb-accent)] bg-[var(--fb-accent-soft)] text-[var(--fb-accent-text)]'
                  : 'border-[var(--fb-border)] bg-[var(--fb-surface)] text-[var(--fb-text-muted)] hover:bg-[var(--fb-surface-muted)]',
              )}
            >
              {filter.label}
            </button>
          ))}
          <Button
            type='button'
            variant='outline'
            onClick={fetchMandates}
            className='h-9 rounded-xl border-[var(--fb-border)] px-3'
          >
            <RefreshCw className='mr-2 size-4' />
            Refresh
          </Button>
        </div>
      </div>

      {filteredMandates.length === 0 ? (
        <div className='rounded-2xl border border-dashed border-[var(--fb-border)] bg-[var(--fb-surface-muted)] px-5 py-10 text-center'>
          <p className='font-DMSans text-base font-semibold text-[var(--fb-text-strong)]'>
            No mandates found
          </p>
          <p className='mt-1 text-sm text-[var(--fb-text-muted)]'>
            {mandates.length === 0
              ? 'Create a mandate for this client to see it here.'
              : 'Try a different search or status filter.'}
          </p>
        </div>
      ) : (
        <Card className='overflow-hidden border border-[var(--fb-border)] bg-[var(--fb-surface)] shadow-[var(--fb-shadow-sm)]'>
          <CardContent className='p-0'>
            <div className='overflow-x-auto'>
              <Table>
                <TableHeader>
                  <TableRow className='border-[var(--fb-border)] bg-[var(--fb-surface-muted)] hover:bg-[var(--fb-surface-muted)]'>
                    <TableHead className='px-4 py-3 text-[var(--fb-text-muted)]'>Mandate</TableHead>
                    <TableHead className='px-4 py-3 text-[var(--fb-text-muted)]'>Amount</TableHead>
                    <TableHead className='px-4 py-3 text-[var(--fb-text-muted)]'>Status</TableHead>
                    <TableHead className='px-4 py-3 text-[var(--fb-text-muted)]'>Bank account</TableHead>
                    <TableHead className='px-4 py-3 text-right text-[var(--fb-text-muted)]'>
                      Created
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMandates.map((mandate) => {
                    const bankLogo = getBankLogo(mandate.ifscCode)
                    return (
                      <TableRow
                        key={mandate.id || mandate.mandateId}
                        className='border-[var(--fb-border)]'
                      >
                        <TableCell className='px-4 py-4'>
                          <div className='flex min-w-0 items-start gap-3'>
                            <div className='flex size-10 shrink-0 items-center justify-center rounded-xl border border-[var(--fb-border)] bg-[var(--fb-surface-muted)]'>
                              {bankLogo ? (
                                <img src={bankLogo} alt='' className='size-5' />
                              ) : (
                                <Landmark className='size-4 text-[var(--fb-accent-text)]' />
                              )}
                            </div>
                            <div className='min-w-0'>
                              <p className='font-DMSans text-sm font-semibold text-[var(--fb-text-strong)]'>
                                {mandate.mandateId || '—'}
                              </p>
                              <p className='mt-0.5 text-xs text-[var(--fb-text-muted)]'>
                                {mandate.mandateType || 'UNIVERSAL'} · {mandate.sipType || 'XSIP'}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className='px-4 py-4 font-DMSans text-sm font-semibold text-[var(--fb-text-strong)]'>
                          ₹ {toCurrency(Number(mandate.amount || 0))}
                        </TableCell>
                        <TableCell className='px-4 py-4'>
                          <span
                            className={cn(
                              'inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold',
                              getStatusStyles(mandate.mandateStatus),
                            )}
                          >
                            {formatStatusLabel(mandate.mandateStatus)}
                          </span>
                        </TableCell>
                        <TableCell className='px-4 py-4'>
                          <p className='text-sm font-medium text-[var(--fb-text-strong)]'>
                            {maskAccount(mandate.accountNumber)}
                          </p>
                          <p className='mt-0.5 text-xs text-[var(--fb-text-muted)]'>
                            {mandate.ifscCode || '—'}
                          </p>
                        </TableCell>
                        <TableCell className='px-4 py-4 text-right text-sm text-[var(--fb-text-muted)]'>
                          {formatCreatedAt(mandate.createdTimeStamp)}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

const AdminMandates = () => {
  const customerDetails = sessionStorage.getItem('actionPlanUser')
  const user = customerDetails ? JSON.parse(customerDetails)?.data : null
  const clientCode = user?.uid?.split('-')[0]?.toUpperCase()
  const customerName = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
  const [activeTab, setActiveTab] = useState('all-mandates')
  const [refreshKey, setRefreshKey] = useState(0)

  return (
    <div className='mx-auto w-full max-w-none px-1 pb-8 pt-2 md:px-2'>
      <div className='mb-5 md:mb-6'>
        <p className='mb-1 text-xs font-medium uppercase tracking-[0.08em] text-[var(--fb-text-muted)]'>
          Admin tools
        </p>
        <h1 className='font-DMSans text-2xl font-bold tracking-tight text-[var(--fb-text-strong)] md:text-3xl'>
          Mandates
        </h1>
        <p className='mt-1 text-sm text-[var(--fb-text-muted)]'>
          {clientCode
            ? `View and create bank mandates${customerName ? ` for ${customerName}` : ''
            } · ${clientCode}`
            : 'Select a client from Plans to view and create mandates.'}
        </p>
      </div>

      {clientCode ? (
        <Tabs value={activeTab} onValueChange={setActiveTab} className='relative w-full'>
          <TabsList className='fabits-holdings-tabs mb-4 flex h-auto w-full justify-start gap-1 overflow-x-auto rounded-[999px] border-0 bg-[var(--fb-surface-muted)] p-1 shadow-none'>
            <TabsTrigger
              value='all-mandates'
              className='fabits-pill-trigger h-9 min-w-max flex-1 rounded-full border-0 px-3 text-xs font-semibold text-[var(--fb-text-muted)] shadow-none md:h-10 md:px-4 md:text-sm data-[state=active]:bg-[var(--fb-accent)] data-[state=active]:text-white data-[state=active]:shadow-[var(--fb-shadow-blue)]'
            >
              All Mandates
            </TabsTrigger>
            <TabsTrigger
              value='create-mandate'
              className='fabits-pill-trigger h-9 min-w-max flex-1 rounded-full border-0 px-3 text-xs font-semibold text-[var(--fb-text-muted)] shadow-none md:h-10 md:px-4 md:text-sm data-[state=active]:bg-[var(--fb-accent)] data-[state=active]:text-white data-[state=active]:shadow-[var(--fb-shadow-blue)]'
            >
              Create Mandate
            </TabsTrigger>
          </TabsList>

          <TabsContent value='all-mandates' className='mt-4 w-full'>
            <UserMandatesList clientCode={clientCode} refreshKey={refreshKey} />
          </TabsContent>

          <TabsContent value='create-mandate' className='mt-4 w-full'>
            <CreateMandate
              clientCode={clientCode}
              embedded
              onSuccess={() => {
                setRefreshKey((value) => value + 1)
                setActiveTab('all-mandates')
              }}
            />
          </TabsContent>
        </Tabs>
      ) : (
        <div className='rounded-2xl border border-dashed border-[var(--fb-border)] bg-[var(--fb-surface-muted)] px-5 py-10 text-center'>
          <p className='font-DMSans text-base font-semibold text-[var(--fb-text-strong)]'>
            No client selected
          </p>
          <p className='mt-1 text-sm text-[var(--fb-text-muted)]'>
            Open a user from Plans first, then return here to manage their mandates.
          </p>
        </div>
      )}
    </div>
  )
}

export default AdminMandates
